import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, TrendingUp, Briefcase, ChevronDown, ChevronUp,
  Target, AlertCircle, MapPin, BarChart2, GitBranch, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { JourneySection } from './JourneySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── HEAT CONFIG ──────────────────────────────────────────────────────────────
const getHeatConfig = (heatLevel) => {
  if (heatLevel === 'high') return {
    tileBg: '#ffffff', border: 'rgba(232,160,32,0.50)',
    glow: '0 0 0 1px rgba(232,160,32,0.18), 0 4px 18px rgba(232,160,32,0.10)',
    barColor: 'linear-gradient(90deg, #8EE5B4, #5BBF80, #3FA86A)',
    badgeColor: '#C47A00', badgeBg: 'rgba(232,160,32,0.12)',
    badgeBorder: 'rgba(232,160,32,0.25)', textColor: '#C47A00',
  };
  if (heatLevel === 'medium') return {
    tileBg: '#ffffff', border: 'rgba(232,160,32,0.22)',
    glow: '0 0 0 1px rgba(232,160,32,0.08)',
    barColor: 'linear-gradient(90deg, rgba(232,160,32,0.65), rgba(232,160,32,0.20))',
    badgeColor: '#9A6200', badgeBg: 'rgba(232,160,32,0.08)',
    badgeBorder: 'rgba(232,160,32,0.15)', textColor: '#9A6200',
  };
  return {
    tileBg: '#ffffff', border: 'rgba(0,0,0,0.08)', glow: 'none',
    barColor: 'linear-gradient(90deg, rgba(0,0,0,0.12), rgba(0,0,0,0.04))',
    badgeColor: 'rgba(0,0,0,0.40)', badgeBg: 'rgba(0,0,0,0.04)',
    badgeBorder: 'rgba(0,0,0,0.08)', textColor: 'rgba(0,0,0,0.40)',
  };
};

// ─── CAREER TILE ──────────────────────────────────────────────────────────────
export const CareerTile = ({ career, isSelected, onClick, filterActive }) => {
  const config = getHeatConfig(career.heat_level);
  const probPercent = Math.round(career.probability * 100);
  const isFiltered = filterActive && career.heat_level !== filterActive;

  return (
    <motion.button
      data-testid={`career-tile-${career.id}`}
      onClick={() => onClick(career)}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: isFiltered ? 0.25 : 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        backgroundColor: config.tileBg,
        border: `1.5px solid ${isSelected ? '#E8A020' : config.border}`,
        borderRadius: '12px', padding: '16px', cursor: 'pointer',
        textAlign: 'left', width: '100%',
        boxShadow: isSelected
          ? '0 0 0 2px rgba(232,160,32,0.18), 0 4px 18px rgba(232,160,32,0.10)'
          : career.heat_level === 'high' ? config.glow : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
        position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={e => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = career.heat_level === 'low' ? 'rgba(0,0,0,0.16)' : 'rgba(232,160,32,0.45)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.10)';
        }
      }}
      onMouseLeave={e => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = config.border;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = career.heat_level === 'high' ? config.glow : '0 1px 3px rgba(0,0,0,0.06)';
        }
      }}
    >
      {career.heat_level === 'high' && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
          background: 'linear-gradient(90deg, #8EE5B4, #5BBF80, #3FA86A)' }} />
      )}
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600,
        color: '#111111', lineHeight: '1.3', marginBottom: '8px' }}>
        {career.title}
      </p>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px',
        backgroundColor: config.badgeBg, border: `1px solid ${config.badgeBorder}`,
        borderRadius: '9999px', padding: '3px 8px', marginBottom: '9px' }}>
        <TrendingUp size={10} color={config.badgeColor} />
        <span data-testid="career-tile-probability" style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600,
          color: config.textColor, fontVariantNumeric: 'tabular-nums' }}>
          {probPercent}% match
        </span>
      </div>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
        color: 'rgba(0,0,0,0.40)', marginBottom: '10px' }}>
        {career.ctc_range}
      </p>
      <div data-testid="career-tile-heatbar" style={{
        height: '4px', backgroundColor: '#eeeeee', borderRadius: '9999px', overflow: 'hidden' }}>
        <div className={career.heat_level === 'high' ? 'heat-bar-high' : ''}
          style={{ height: '100%', width: `${probPercent}%`, background: config.barColor,
            borderRadius: '9999px', transition: 'width 0.6s ease', maxWidth: '100%' }} />
      </div>
    </motion.button>
  );
};

// ─── ROADMAP STEP ─────────────────────────────────────────────────────────────
const RoadmapStep = ({ step, isLast }) => (
  <div style={{ display: 'flex', gap: '12px' }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
        backgroundColor: 'rgba(232,160,32,0.10)', border: '1.5px solid rgba(232,160,32,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#C47A00',
      }}>{step.step}</div>
      {!isLast && <div style={{ flex: 1, width: '1px', backgroundColor: 'rgba(232,160,32,0.20)', marginTop: '4px', minHeight: '24px' }} />}
    </div>
    <div style={{ paddingBottom: isLast ? 0 : '20px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600, color: '#111111' }}>{step.title}</p>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', color: 'rgba(0,0,0,0.40)',
          backgroundColor: '#f5f5f5', border: '1px solid rgba(0,0,0,0.07)', borderRadius: '9999px',
          padding: '2px 7px', whiteSpace: 'nowrap' }}>{step.timeline}</span>
      </div>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.50)', lineHeight: '1.5' }}>
        {step.description}
      </p>
    </div>
  </div>
);

// ─── SPIDER / RADAR CHART — responsive SVG ───────────────────────────────────
const SpiderChart = ({ competencies }) => {
  if (!competencies || competencies.length === 0) return null;
  const N       = competencies.length;
  const SIZE    = 300;
  const CX      = SIZE / 2;
  const CY      = SIZE / 2;
  const R       = 95;
  const LABEL_R = R + 50;
  const LH      = 15;
  const PAD_X   = 115; const PAD_Y = 80;
  const VW = SIZE + PAD_X * 2; const VH = SIZE + PAD_Y * 2;
  const OX = -PAD_X; const OY = -PAD_Y;

  const angle   = (i) => (i * 2 * Math.PI / N) - Math.PI / 2;
  const toXY    = (i, r) => [CX + r * Math.cos(angle(i)), CY + r * Math.sin(angle(i))];
  const polyPts = (scores, max = 10) =>
    scores.map((s, i) => toXY(i, R * (s / max)).join(',')).join(' ');

  const textAnchor = (i) => {
    const cos = Math.cos(angle(i));
    if (cos > 0.2) return 'start';
    if (cos < -0.2) return 'end';
    return 'middle';
  };

  const labelLines = (name) => {
    const words = name.split(' ');
    if (words.length <= 2) return [name];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  };

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <svg
        viewBox={`${OX} ${OY} ${VW} ${VH}`}
        style={{ display: 'block', width: '100%', maxWidth: '520px', margin: '0 auto' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {[0.25, 0.5, 0.75, 1.0].map((r, ri) => (
          <polygon key={ri}
            points={Array.from({ length: N }, (_, i) => toXY(i, R * r).join(',')).join(' ')}
            fill="none"
            stroke={ri === 3 ? 'rgba(0,0,0,0.13)' : 'rgba(0,0,0,0.07)'}
            strokeWidth="1" strokeDasharray={ri < 3 ? '4 3' : '0'} />
        ))}
        {Array.from({ length: N }, (_, i) => {
          const [x, y] = toXY(i, R);
          return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="rgba(0,0,0,0.08)" strokeWidth="1" />;
        })}
        <polygon points={polyPts(competencies.map(c => c.excellence))}
          fill="rgba(63,168,106,0.07)" stroke="#3FA86A" strokeWidth="1.5" strokeDasharray="6 3" />
        <polygon points={polyPts(competencies.map(c => c.current))}
          fill="rgba(232,160,32,0.14)" stroke="#E8A020" strokeWidth="2.5" />
        {competencies.map((c, i) => {
          const [x, y] = toXY(i, R * ((c.current ?? 0) / 10));
          return <circle key={i} cx={x} cy={y} r="5" fill="#E8A020" stroke="#fff" strokeWidth="2" />;
        })}
        {[0.25, 0.5, 0.75, 1.0].map((r, ri) => {
          const [, ly] = toXY(0, R * r);
          const lbl = ri === 0 ? '2.5' : ri === 1 ? '5' : ri === 2 ? '7.5' : '10';
          return <text key={ri} x={CX + 5} y={ly + 4} fontFamily="DM Sans, sans-serif" fontSize="9" fill="rgba(0,0,0,0.28)" textAnchor="start">{lbl}</text>;
        })}
        {competencies.map((c, i) => {
          const [lx, ly] = toXY(i, LABEL_R);
          const anchor   = textAnchor(i);
          const lines    = labelLines(c.name);
          const startY   = ly - (lines.length * LH) / 2 + LH / 2;
          return (
            <g key={i}>
              {lines.map((line, li) => (
                <text key={li} x={lx} y={startY + li * LH} textAnchor={anchor} dominantBaseline="middle"
                  fontFamily="DM Sans, sans-serif" fontSize="11.5" fontWeight="600" fill="rgba(0,0,0,0.75)">
                  {line}
                </text>
              ))}
              <text x={lx} y={startY + lines.length * LH + 5} textAnchor={anchor} dominantBaseline="middle"
                fontFamily="DM Sans, sans-serif" fontSize="11" fontWeight="700" fill="#E8A020">
                {`${c.current != null ? c.current : '?'}/10`}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ─── CAREER HEALTH — 4-card insight grid ─────────────────────────────────────
export const CareerHealth = ({ careerHealth, earningPotentialMax }) => {
  if (!careerHealth?.working_for_you?.headline) return null;

  const cards = [
    {
      data:       careerHealth.working_for_you,
      title:      "What's already working in your favour",
      arrowType:  'up',
      textColor:  '#111111',
      arrowColor: '#2A7050',
    },
    {
      data:       careerHealth.market_reward,
      title:      "What the market will reward you for",
      arrowType:  'up',
      textColor:  '#111111',
      arrowColor: '#2A7050',
    },
    {
      data:       careerHealth.breakout_potential,
      title:      "What gives you breakout potential",
      arrowType:  'spark',
      textColor:  '#C47A00',
      arrowColor: '#E8A020',
    },
    {
      data:       careerHealth.hold_back,
      title:      "What could hold you back",
      arrowType:  'down',
      textColor:  '#C03030',
      arrowColor: '#D94040',
    },
  ];

  // Directional icons: up (positive), down (negative), spark (potential/opportunity)
  const ArrowIcon = ({ type, color }) => {
    if (type === 'up') return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 14l10-10M14 14V4H4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    if (type === 'down') return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 6l10 10M14 6v10H4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    // spark = lightning bolt for breakout potential
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M11.5 2L5 11h5.5L8.5 18 16 9h-5.5L11.5 2z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 }}
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '24px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* ── Section header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px',
        borderBottom: '1px solid rgba(0,0,0,0.07)',
        flexWrap: 'wrap', gap: '8px',
      }}>
        <div>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(0,0,0,0.38)', marginBottom: '5px',
          }}>
            KEY FINDINGS
          </p>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '20px', fontWeight: 700,
            color: '#111111', margin: 0, letterSpacing: '-0.01em',
          }}>
            Your career health{' '}
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
              at a glance
            </span>
          </p>
        </div>
        {earningPotentialMax && (
          <div className="ch-earning" style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '26px', fontWeight: 900, color: '#2A7050', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '2px' }}>
              ₹{earningPotentialMax}L
            </p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#3FA86A', margin: 0 }}>
              Post-PGP Potential
            </p>
          </div>
        )}
      </div>

      {/* ── 4 insight cards — responsive grid ── */}
      <div className="career-health-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
      }}>
        {cards.map((card, i) => (
          <div key={i} className={`ch-card ch-card-${i}`} style={{
            padding: '22px 20px 18px',
            borderRight: i < 3 ? '1px solid rgba(0,0,0,0.07)' : 'none',
            display: 'flex', flexDirection: 'column', gap: '12px',
            minWidth: 0, overflow: 'hidden',
          }}>
            {/* Top row: icon + arrow */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '6px',
                backgroundColor: `${card.arrowColor}14`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="1" y="7" width="3" height="6" fill={card.arrowColor} opacity="0.7" rx="0.5"/>
                  <rect x="5.5" y="4" width="3" height="9" fill={card.arrowColor} opacity="0.85" rx="0.5"/>
                  <rect x="10" y="1" width="3" height="12" fill={card.arrowColor} rx="0.5"/>
                </svg>
              </div>
              <ArrowIcon type={card.arrowType} color={card.arrowColor} />
            </div>

            {/* Card title */}
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
              letterSpacing: '0.09em', textTransform: 'uppercase',
              color: 'rgba(0,0,0,0.35)', margin: 0, lineHeight: 1.4,
            }}>
              {card.title}
            </p>

            {/* Main insight text — the hero */}
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700,
              color: card.textColor, lineHeight: 1.55,
              margin: 0, flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word',
            }}>
              {card.data.headline}
            </p>

            {/* Label */}
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px',
              color: 'rgba(0,0,0,0.32)', fontStyle: 'italic',
              margin: 0, paddingTop: '10px',
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}>
              {card.data.label}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};


// ─── VERDICT — CTA section for PGP TBM ───────────────────────────────────────
export const Verdict = ({ verdict, candidateName, analysisId }) => {
  if (!verdict?.headline) return null;
  const { headline, pre_text, program_fit, key_benefits = [] } = verdict;
  const firstName = candidateName?.split(' ')[0] || 'You';

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        background: 'radial-gradient(ellipse 110% 60% at 50% 0%, #231B0D 0%, #141109 40%, #060504 70%, #0C0C0C 100%)',
        borderRadius: '16px', padding: '28px 24px',
        color: '#ffffff', position: 'relative', overflow: 'hidden',
        marginBottom: '24px',
      }}
    >
      {/* Ambient glow */}
      <div style={{ position: 'absolute', right: '-60px', top: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,160,32,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />

      {/* Kicker — "The Verdict" only, no PGP badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#E8A020' }}>
          The Verdict
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
      </div>

      {/* Headline */}
      <h2 style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(20px, 2.8vw, 30px)', fontWeight: 700,
        color: '#ffffff', lineHeight: 1.2, letterSpacing: '-0.02em',
        marginBottom: '14px',
      }}>
        {headline}
      </h2>

      {/* Pre-text — contextualises the transition + why PGP TBM fits */}
      {pre_text && (
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.80)', lineHeight: 1.65, marginBottom: '12px', fontStyle: 'italic' }}>
          {pre_text}
        </p>
      )}

      {/* Programme fit text */}
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.90)', lineHeight: 1.7, marginBottom: '20px' }}>
        {program_fit}
      </p>

      {/* Key benefits chips */}
      {key_benefits.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '22px' }}>
          {key_benefits.map((b, i) => (
            <span key={i} style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 600,
              color: 'rgba(255,255,255,0.70)',
              backgroundColor: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '9999px', padding: '5px 12px',
            }}>
              {b}
            </span>
          ))}
        </div>
      )}

      {/* Divider + CTA row */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 'clamp(14px, 1.8vw, 16px)', color: 'rgba(255,255,255,0.70)', lineHeight: 1.55, margin: 0, flex: 1, minWidth: '200px' }}>
          "{firstName}, your profile is one structured programme away from the title your output already deserves."
        </p>
        <a
          href="https://mastersunion.org/pgp-technology-and-business-management"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px',
            backgroundColor: 'transparent',
            border: '1.5px solid rgba(232,160,32,0.55)',
            borderRadius: '9999px',
            cursor: 'pointer', textDecoration: 'none',
            fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600, color: '#E8A020',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(232,160,32,0.12)'; e.currentTarget.style.borderColor = 'rgba(232,160,32,0.80)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(232,160,32,0.55)'; }}
        >
          Learn about PGP TBM
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M2 6.5h9M8 3.5l3 3-3 3" stroke="#E8A020" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
      {/* ── Journey sub-section ── */}
      <JourneySection analysisId={analysisId} />
    </motion.div>
  );
}; 

// ─── EARNING GAP ALERT — standalone banner ────────────────────────────────────
export const EarningGapAlert = ({ earningsGap }) => {
  if (!earningsGap?.is_underpaid || !earningsGap?.gap_percent) return null;
  const { current_ctc_lpa, industry_median_lpa, median_gap_lpa, gap_percent } = earningsGap;
  if (!current_ctc_lpa || current_ctc_lpa === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '14px',
      backgroundColor: 'rgba(217,64,64,0.05)',
      border: '1.5px solid rgba(217,64,64,0.22)',
      borderRadius: '12px', padding: '16px 20px', marginBottom: '14px',
    }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
        backgroundColor: 'rgba(217,64,64,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontFamily: 'DM Sans', fontSize: '20px', fontWeight: 900, color: '#D94040', lineHeight: 1 }}>!</span>
      </div>
      <div>
        <p style={{ fontFamily: 'DM Sans', fontSize: '18px', fontWeight: 800, color: '#D94040', marginBottom: '5px', lineHeight: 1.2 }}>
          You're underpaid by ~{gap_percent}%
        </p>
        <p style={{ fontFamily: 'DM Sans', fontSize: '14.5px', color: 'rgba(0,0,0,0.55)', margin: 0, lineHeight: 1.5 }}>
          Your current CTC of ₹{current_ctc_lpa}L is below the industry median of ₹{industry_median_lpa}L.
          {median_gap_lpa > 0 && <> Gap: <span style={{ color: '#D94040', fontWeight: 700 }}>₹{median_gap_lpa}L/year</span>.</>}
        </p>
      </div>
    </div>
  );
};



export const EarningGap = ({ earningsGap }) => {
  if (!earningsGap?.verdict && !earningsGap?.market_rate_min) return null;
  const {
    current_ctc_lpa, market_rate_min, market_rate_max,
    p25_lpa, industry_median_lpa, p75_lpa,
    post_mba_min, post_mba_max,
    median_gap_lpa, gap_percent, is_underpaid,
    verdict, reasoning, market_rate_note,
  } = earningsGap;

  // Safe string extraction — LLM occasionally returns the verdict object
  // instead of a plain string, which would crash React's render
  const verdictStr  = typeof verdict  === 'string' ? verdict  : (verdict?.headline  || '');
  const reasoningStr = typeof reasoning === 'string' ? reasoning : '';

  const isStudent = !current_ctc_lpa || current_ctc_lpa === 0;
  const gapColor  = gap_percent >= 40 ? '#D94040' : gap_percent >= 20 ? '#E8A020' : '#2A7050';

  // ── Salary position bar ─────────────────────────────────────────────────────
  const SalaryBar = () => {
    if (isStudent || !p25_lpa || !industry_median_lpa) return null;
    const barMax  = Math.max((post_mba_max || 50) * 1.18, (p75_lpa || 35) * 1.5, 55);
    const pct     = (v) => Math.min(Math.max((v / barMax) * 100, 1), 97);
    const youPct  = pct(current_ctc_lpa);
    const pgpPct  = pct(post_mba_max || 45);
    const p25Pct  = pct(p25_lpa);
    const p50Pct  = pct(industry_median_lpa);
    const p75Pct  = pct(p75_lpa);

    return (
      <div style={{ marginBottom: '24px', paddingTop: '8px' }}>
        <p style={{ fontFamily: 'DM Sans', fontSize: '14px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '32px' }}>
          Your salary position
        </p>
        {/* Bar container */}
        <div style={{ position: 'relative', paddingBottom: '60px' }}>
          {/* YOU pill */}
          <div style={{
            position: 'absolute', left: `${youPct}%`, top: '-28px',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #E8A020 0%, #8EE5B4 55%, #3FA86A 100%)',
            borderRadius: '7px', padding: '5px 10px',
            fontFamily: 'DM Sans', fontSize: '14px', fontWeight: 800, color: '#000',
            whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', zIndex: 2,
          }}>
            YOU: ₹{current_ctc_lpa}L
          </div>

          {/* POST-PGP pill */}
          <div style={{
            position: 'absolute', left: `${pgpPct}%`, top: '-28px',
            transform: 'translateX(-50%)',
            background: '#2A7050', borderRadius: '7px', padding: '5px 10px',
            fontFamily: 'DM Sans', fontSize: '14px', fontWeight: 800, color: '#fff',
            whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(42,112,80,0.25)', zIndex: 2,
          }}>
            POST-PGP: ₹{post_mba_max}L
          </div>

          {/* Track */}
          <div style={{ position: 'relative', height: '10px', backgroundColor: '#eeeeee', borderRadius: '9999px' }}>
            {/* Lavender fill to YOU position */}
            <div style={{
              position: 'absolute', left: 0, top: 0, height: '100%',
              width: `${youPct}%`,
              background: 'rgba(149,130,212,0.28)',
              borderRadius: '9999px',
            }} />
            {/* YOU stem */}
            <div style={{
              position: 'absolute', left: `${youPct}%`, top: '100%',
              width: '2px', height: '14px',
              background: 'linear-gradient(to bottom, #E8A020, transparent)',
              transform: 'translateX(-50%)',
            }} />
            {/* POST-PGP stem */}
            <div style={{
              position: 'absolute', left: `${pgpPct}%`, top: '100%',
              width: '2px', height: '14px',
              background: 'linear-gradient(to bottom, #2A7050, transparent)',
              transform: 'translateX(-50%)',
            }} />
            {/* Percentile tick marks */}
            {[{ pct: p25Pct, label: '25th', val: p25_lpa }, { pct: p50Pct, label: '50th', val: industry_median_lpa }, { pct: p75Pct, label: '75th', val: p75_lpa }].map((tick) => (
              <div key={tick.label}>
                <div style={{
                  position: 'absolute', left: `${tick.pct}%`, top: '-3px',
                  width: '1.5px', height: '16px',
                  backgroundColor: 'rgba(0,0,0,0.15)',
                  transform: 'translateX(-50%)',
                }} />
                <div style={{
                  position: 'absolute', left: `${tick.pct}%`, top: '18px',
                  transform: 'translateX(-50%)', textAlign: 'center',
                }}>
                  <div style={{ fontFamily: 'DM Sans', fontSize: '10px', color: 'rgba(0,0,0,0.35)', fontWeight: 500, marginBottom: '3px' }}>
                    {tick.label}
                  </div>
                  <div style={{ fontFamily: 'DM Sans', fontSize: '14px', fontWeight: 800, color: '#111', whiteSpace: 'nowrap' }}>
                    ₹{tick.val}L
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Stat boxes ───────────────────────────────────────────────────────────────
  const StatBox = ({ label, value, sub, accent, bg, border }) => (
    <div style={{
      flex: '1 1 160px', backgroundColor: bg || '#ffffff',
      border: `1.5px solid ${border || 'rgba(0,0,0,0.08)'}`,
      borderRadius: '14px', padding: '20px 18px',
      display: 'flex', flexDirection: 'column', gap: '4px',
    }}>
      <span style={{ fontFamily: 'DM Sans', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.38)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'DM Sans', fontSize: 'clamp(26px,3.5vw,38px)', fontWeight: 800, color: accent || '#111111', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
        {value}
      </span>
      {sub && <span style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.40)', lineHeight: 1.4 }}>{sub}</span>}
    </div>
  );

  const Arrow = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '0 2px' }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M5 12h14M13 6l6 6-6 6" stroke="rgba(0,0,0,0.18)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* ── Main cream card ── */}
      <div style={{
        background: 'linear-gradient(135deg, #FFFDF7 0%, #FFF8EC 50%, #FFFDF7 100%)',
        border: '1.5px solid rgba(232,160,32,0.20)', borderRadius: '16px',
        padding: '28px 28px 20px', marginBottom: '24px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,160,32,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Header */}
        <div style={{ marginBottom: '22px' }}>
          <h3 style={{ fontFamily: 'DM Sans', fontSize: '22px', fontWeight: 700, color: '#111111', marginBottom: '4px', letterSpacing: '-0.01em' }}>
            Earning{'  '}
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
              Potential Gap
            </span>
          </h3>
          <p style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.45)', margin: 0 }}>
            Based on Michael Page India Salary Guide 2026 · current market benchmarks
          </p>
        </div>

        {/* Three stat boxes */}
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '6px', flexWrap: 'wrap', marginBottom: '24px' }}>
          <StatBox
            label="You currently earn"
            value={isStudent ? 'Entry' : `₹${current_ctc_lpa}L`}
            sub={isStudent ? 'Student / pre-career' : 'per year (declared)'}
            bg="#ffffff" border="rgba(0,0,0,0.10)"
          />
          <Arrow />
          <StatBox
            label="Market rate for your profile"
            value={`₹${market_rate_min}–${market_rate_max}L`}
            sub={market_rate_note || '2026 benchmarks, similar exp.'}
            accent="#C47A00" bg="rgba(232,160,32,0.04)" border="rgba(232,160,32,0.22)"
          />
          <Arrow />
          <StatBox
            label="Post-PGP potential"
            value={`₹${post_mba_min}–${post_mba_max}L`}
            sub="top career match, within 2 yrs"
            accent="#2A7050" bg="rgba(63,168,106,0.05)" border="rgba(63,168,106,0.22)"
          />
        </div>

        {/* Salary position bar */}
        <SalaryBar />

        {/* Gap highlight — verdict + reasoning */}
        {is_underpaid && gap_percent > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '20px',
            backgroundColor: '#ffffff',
            border: `1.5px solid ${gapColor}28`,
            borderLeft: `4px solid ${gapColor}`,
            borderRadius: '10px', padding: '18px 20px',
            marginBottom: '16px', flexWrap: 'wrap',
          }}>
            <div style={{ flexShrink: 0, minWidth: '80px', textAlign: 'center' }}>
              <span style={{ fontFamily: 'DM Sans', fontSize: 'clamp(36px,5vw,52px)', fontWeight: 900, color: gapColor, lineHeight: 1, letterSpacing: '-0.03em', display: 'block' }}>
                {gap_percent}%
              </span>
              <span style={{ fontFamily: 'DM Sans', fontSize: '14.5px', fontWeight: 600, color: gapColor }}>
                Below market rate
              </span>
            </div>
            <div style={{ flex: 1, minWidth: '200px', borderLeft: '1px solid rgba(0,0,0,0.08)', paddingLeft: '18px' }}>
              <p style={{
                fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic',
                fontSize: 'clamp(17px, 2.2vw, 22px)', color: '#111111',
                lineHeight: 1.45, margin: '0 0 10px 0', fontWeight: 600,
              }}>
                "{verdictStr}"
              </p>
              <p style={{ fontFamily: 'DM Sans', fontSize: '14.5px', color: 'rgba(0,0,0,0.58)', lineHeight: 1.65, margin: 0 }}>
                {reasoningStr}
              </p>
            </div>
          </div>
        )}

        {/* Student / no-gap */}
        {(!is_underpaid || isStudent) && verdictStr && (
          <div style={{ backgroundColor: '#ffffff', border: '1px solid rgba(63,168,106,0.20)', borderLeft: '4px solid #2A7050', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '20px', color: '#111', lineHeight: 1.5, margin: '0 0 8px 0' }}>"{verdictStr}"</p>
            <p style={{ fontFamily: 'DM Sans', fontSize: '14.5px', color: 'rgba(0,0,0,0.55)', lineHeight: 1.65, margin: 0 }}>{reasoningStr}</p>
          </div>
        )}

        <p style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.28)', margin: 0, textAlign: 'right' }}>
          Source: Michael Page India Salary Guide 2026 · AI-augmented market data
        </p>
      </div>
    </motion.div>
  );
};


export const ProfileSnapshot = ({ snapshot }) => {
  if (!snapshot?.headline) return null;
  const { headline, strengths = [], critical_gap, unlock } = snapshot;

  return (
    <div style={{
      background: 'radial-gradient(ellipse 110% 60% at 50% 0%, #231B0D 0%, #141109 40%, #060504 70%, #0C0C0C 100%)',
      borderRadius: '16px',
      padding: '36px 32px',
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Subtle texture overlay */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '16px',
        backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(232,160,32,0.06) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* Kicker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px' }}>
        <span style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: '#E8A020',
        }}>
          The Snapshot
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
      </div>

      {/* Main headline — full width, no maxWidth constraint */}
      <h2 style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(22px, 2.8vw, 32px)',
        fontWeight: 700,
        color: '#ffffff',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        marginBottom: '32px',
      }}>
        {headline}
      </h2>

      {/* Strengths — "Where you're already dangerous" */}
      {strengths.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.28)', marginBottom: '12px',
          }}>
            Where you're already dangerous
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {strengths.map((s, i) => (
              <div key={i} style={{
                flex: '1 1 200px',
                background: 'rgba(232,160,32,0.08)',
                border: '1px solid rgba(232,160,32,0.25)',
                borderRadius: '12px',
                padding: '18px 20px',
              }}>
                {/* Big score */}
                <div style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '40px', fontWeight: 800,
                  color: '#E8A020', lineHeight: 1,
                  marginBottom: '8px',
                  letterSpacing: '-0.03em',
                }}>
                  {s.score}<span style={{ fontSize: '18px', color: 'rgba(232,160,32,0.45)', fontWeight: 600 }}>/10</span>
                </div>
                <p style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
                  color: '#ffffff', marginBottom: '6px', lineHeight: 1.2,
                }}>
                  {s.name}
                </p>
                <p style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
                  color: 'rgba(255,255,255,0.88)', lineHeight: 1.6, margin: 0,
                }}>
                  {s.line}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Critical gap */}
      {critical_gap && (
        <div style={{ marginBottom: '28px' }}>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.28)', marginBottom: '12px',
          }}>
            The one thing to fix
          </p>
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
          }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '40px', fontWeight: 800,
                color: 'rgba(255,255,255,0.35)', lineHeight: 1,
                letterSpacing: '-0.03em',
              }}>
                {critical_gap.score}<span style={{ fontSize: '18px', fontWeight: 600 }}>/10</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '160px' }}>
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
                color: '#ffffff', marginBottom: '5px',
              }}>
                {critical_gap.name}
              </p>
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
                color: 'rgba(255,255,255,0.88)', lineHeight: 1.6, margin: 0,
              }}>
                {critical_gap.line}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Unlock — italic serif at bottom */}
      {unlock && (
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.07)',
          paddingTop: '22px',
        }}>
          <p style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontStyle: 'italic',
            fontSize: 'clamp(17px, 2vw, 21px)',
            color: 'rgba(255,255,255,0.80)',
            lineHeight: 1.6,
            margin: 0,
          }}>
            {unlock}
          </p>
        </div>
      )}
    </div>
  );
};
// ─── SKILL ASSESSMENT SECTION (exported for main results page) ───────────────
export const SkillAssessment = ({ skillAssessment, profileSnapshot }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const raw           = skillAssessment || {};
  const competencies  = Array.isArray(raw) ? raw : (raw.competencies || []);
  const signals       = Array.isArray(raw) ? [] : (raw.signals || []);
  const opportunities = Array.isArray(raw) ? [] : (raw.opportunities || []);
  if (!competencies.length) return null;

  return (
    <div>
      {/* ── Radar chart + legend (always visible) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
        <SpiderChart competencies={competencies} />
        <div style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '20px', height: '2.5px', backgroundColor: '#E8A020', borderRadius: '1px' }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.50)' }}>Your current level</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '20px', height: '0', borderTop: '2px dashed #3FA86A' }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.50)' }}>Role excellence</span>
          </div>
        </div>
      </div>

      {/* ── The Snapshot — embedded after radar ── */}
      {profileSnapshot?.headline && (
        <div style={{ marginBottom: '20px' }}>
          <ProfileSnapshot snapshot={profileSnapshot} />
        </div>
      )}

      {/* ── Toggle — more prominent amber design ── */}
      <button
        onClick={() => setDetailsOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          width: '100%', padding: '14px 18px',
          background: detailsOpen ? 'rgba(232,160,32,0.08)' : 'rgba(232,160,32,0.05)',
          border: `1.5px solid ${detailsOpen ? 'rgba(232,160,32,0.45)' : 'rgba(232,160,32,0.30)'}`,
          borderRadius: '10px', cursor: 'pointer',
          fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
          color: '#9A6200',
          marginBottom: detailsOpen ? '14px' : '0',
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,160,32,0.10)'; e.currentTarget.style.borderColor = 'rgba(232,160,32,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = detailsOpen ? 'rgba(232,160,32,0.08)' : 'rgba(232,160,32,0.05)'; e.currentTarget.style.borderColor = detailsOpen ? 'rgba(232,160,32,0.45)' : 'rgba(232,160,32,0.30)'; }}
      >
        <span style={{ fontSize: '14px' }}>{detailsOpen ? '−' : '+'}</span>
        <span style={{ flex: 1, textAlign: 'left' }}>
          {detailsOpen ? 'Hide' : 'View'} detailed competency breakdown
        </span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
          style={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', flexShrink: 0 }}>
          <path d="M2 5l5 5 5-5" stroke="#9A6200" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Collapsible detail */}
      <AnimatePresence>
        {detailsOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {competencies.map((c, i) => {
                const gap        = c.excellence - c.current;
                const pctCurrent = (c.current   / 10) * 100;
                const pctExcel   = (c.excellence / 10) * 100;
                return (
                  <div key={i} style={{ backgroundColor: '#f8f8f8', border: '1px solid rgba(0,0,0,0.07)', borderRadius: '10px', padding: '15px 17px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '11px' }}>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#111111' }}>{c.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#ffffff', backgroundColor: '#E8A020', borderRadius: '9999px', padding: '2px 8px', fontVariantNumeric: 'tabular-nums' }}>{c.current}/10</span>
                        {gap > 0 && (<>
                          <span style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.25)', margin: '0 2px' }}>→</span>
                          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 600, color: '#2A6644', backgroundColor: 'rgba(63,168,106,0.12)', border: '1px solid rgba(63,168,106,0.25)', borderRadius: '9999px', padding: '2px 8px', fontVariantNumeric: 'tabular-nums' }}>{c.excellence}/10</span>
                        </>)}
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ position: 'relative', height: '8px', marginBottom: '7px' }}>
                      <div style={{ position: 'absolute', inset: 0, backgroundColor: '#e4e4e4', borderRadius: '9999px' }} />
                      {gap > 0 && (
                        <div style={{ position: 'absolute', left: `${pctCurrent}%`, top: '1px', bottom: '1px', width: `${pctExcel - pctCurrent}%`, background: 'linear-gradient(90deg, rgba(232,160,32,0.22) 0%, rgba(63,168,106,0.28) 100%)', borderRadius: '0 5px 5px 0' }} />
                      )}
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pctCurrent}%`, background: `linear-gradient(90deg, #E8A020 0%, #C8B830 ${(pctCurrent/pctExcel)*45}%, #5BBF80 ${(pctCurrent/pctExcel)*80}%, #3FA86A ${pctExcel}%)`, borderRadius: '9999px', transition: 'width 0.6s ease' }} />
                      <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: `${pctExcel}%`, width: '2.5px', backgroundColor: '#2A6644', transform: 'translateX(-50%)', borderRadius: '2px', zIndex: 2 }} />
                    </div>
                    {/* Mini legend */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '11px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '10px', height: '3px', background: 'linear-gradient(90deg,#E8A020,#3FA86A)', borderRadius: '1px' }} />
                        <span style={{ fontFamily: 'DM Sans', fontSize: '14px', color: '#C47A00', fontWeight: 500 }}>You now</span>
                      </div>
                      {gap > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '10px', height: '3px', background: 'linear-gradient(90deg,rgba(232,160,32,0.30),rgba(63,168,106,0.35))', borderRadius: '1px' }} />
                          <span style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.40)' }}>Gap ({gap} pts)</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '2.5px', height: '10px', backgroundColor: '#2A6644', borderRadius: '1px' }} />
                        <span style={{ fontFamily: 'DM Sans', fontSize: '14px', color: '#2A6644', fontWeight: 500 }}>Excellence</span>
                      </div>
                    </div>
                    {c.insight && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.65)', lineHeight: '1.65', margin: 0 }}>{c.insight}</p>}
                  </div>
                );
              })}
            </div>
            {(signals.length > 0 || opportunities.length > 0) && (
              <div>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.55)', lineHeight: '1.55', marginBottom: '14px', fontStyle: 'italic', borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '18px' }}>
                  What your resume is already signaling well — and what may need to show up more clearly for stronger role fit.
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {signals.length > 0 && (
                    <div style={{ flex: '1 1 240px', backgroundColor: 'rgba(63,168,106,0.05)', border: '1px solid rgba(63,168,106,0.20)', borderRadius: '10px', padding: '15px 17px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, backgroundColor: '#3FA86A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                        <span style={{ fontFamily: 'DM Sans', fontSize: '14.5px', fontWeight: 700, color: '#2A7050', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Signals</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                        {signals.map((s, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#3FA86A', flexShrink: 0, marginTop: '7px' }} />
                            <p style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.65)', lineHeight: '1.55', margin: 0 }}>{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {opportunities.length > 0 && (
                    <div style={{ flex: '1 1 240px', backgroundColor: 'rgba(232,160,32,0.05)', border: '1px solid rgba(232,160,32,0.22)', borderRadius: '10px', padding: '15px 17px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, backgroundColor: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 2v4M5 7.5v.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        </div>
                        <span style={{ fontFamily: 'DM Sans', fontSize: '14.5px', fontWeight: 700, color: '#9A6200', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Opportunities</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                        {opportunities.map((o, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#E8A020', flexShrink: 0, marginTop: '7px' }} />
                            <p style={{ fontFamily: 'DM Sans', fontSize: '14px', color: 'rgba(0,0,0,0.65)', lineHeight: '1.55', margin: 0 }}>{o}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── CAREER TRAJECTORY SECTION ────────────────────────────────────────────────
const CareerTrajectory = ({ trajectory }) => {
  if (!trajectory) return null;
  const { past = [], future = [], mba_required, mba_insight } = trajectory;
  const allStages = [
    ...past.map(s => ({ ...s, type: 'past' })),
    ...future.map(s => ({ ...s, type: s.is_mba ? 'mba' : 'future' })),
  ];

  return (
    <div>
      {/* MBA banner if required */}
      {mba_required && mba_insight && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          backgroundColor: 'rgba(232,160,32,0.08)',
          border: '1px solid rgba(232,160,32,0.25)',
          borderRadius: '10px', padding: '11px 13px', marginBottom: '18px',
        }}>
          <div style={{
            width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '14px', color: '#fff', fontWeight: 700 }}>M</span>
          </div>
          <div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
              color: '#9A6200', marginBottom: '2px' }}>MBA Recommended</p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.55)', lineHeight: '1.45' }}>
              {mba_insight}
            </p>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: '18px' }}>
        {/* Vertical rail */}
        <div style={{
          position: 'absolute', left: '7px', top: '10px', bottom: '10px',
          width: '2px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.10) 100%)',
        }} />

        {allStages.map((stage, i) => {
          const isPast      = stage.type === 'past';
          const isCurrent   = stage.is_current;
          const isMBA       = stage.type === 'mba';
          const isFuture    = stage.type === 'future';
          const isLast      = i === allStages.length - 1;

          const dotColor    = isCurrent ? '#E8A020' : isMBA ? '#E8A020' : isPast ? '#3FA86A' : '#9CA3AF';
          const dotBg       = isCurrent ? '#E8A020' : isMBA ? 'rgba(232,160,32,0.15)' : isPast ? '#3FA86A' : '#f0f0f0';
          const dotBorder   = isCurrent ? '#E8A020' : isMBA ? '#E8A020' : isPast ? '#3FA86A' : 'rgba(0,0,0,0.15)';

          return (
            <div key={i} style={{ display: 'flex', gap: '14px', marginBottom: isLast ? 0 : '14px', position: 'relative' }}>
              {/* Dot */}
              <div style={{
                width: isCurrent ? '16px' : '14px',
                height: isCurrent ? '16px' : '14px',
                borderRadius: '50%', flexShrink: 0,
                backgroundColor: dotBg,
                border: `2px solid ${dotBorder}`,
                marginTop: '3px',
                marginLeft: isCurrent ? '-1px' : '0',
                zIndex: 1,
                boxShadow: isCurrent ? '0 0 0 3px rgba(232,160,32,0.15)' : 'none',
              }}>
                {isCurrent && (
                  <div style={{ width: '100%', height: '100%', borderRadius: '50%',
                    backgroundColor: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#fff' }} />
                  </div>
                )}
                {isMBA && !isCurrent && (
                  <div style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '7px', fontWeight: 700, color: '#E8A020', lineHeight: 1 }}>M</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, paddingTop: '1px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {stage.year && (
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
                      color: 'rgba(0,0,0,0.35)', letterSpacing: '0.04em' }}>
                      {stage.year}
                    </span>
                  )}
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: isCurrent ? 700 : 600,
                    color: isCurrent ? '#111111' : isMBA ? '#9A6200' : isFuture ? 'rgba(0,0,0,0.60)' : '#111111' }}>
                    {stage.role}
                  </span>
                  {isCurrent && (
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
                      color: '#E8A020', backgroundColor: 'rgba(232,160,32,0.10)',
                      border: '1px solid rgba(232,160,32,0.22)', borderRadius: '9999px', padding: '1px 7px' }}>
                      NOW
                    </span>
                  )}
                  {isMBA && (
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600,
                      color: '#9A6200', backgroundColor: 'rgba(232,160,32,0.10)',
                      border: '1px solid rgba(232,160,32,0.22)', borderRadius: '9999px', padding: '1px 7px' }}>
                      MBA
                    </span>
                  )}
                </div>
                {stage.company && (
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.40)', marginTop: '1px' }}>
                    {stage.company}
                  </p>
                )}
                {stage.note && (
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.45)',
                    fontStyle: isFuture || isMBA ? 'italic' : 'normal', marginTop: '2px', lineHeight: '1.4' }}>
                    {stage.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── COLLAPSIBLE SECTION ──────────────────────────────────────────────────────
const Section = ({ icon: Icon, iconColor, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '0 0 10px 0',
          borderBottom: '1px solid rgba(0,0,0,0.07)', marginBottom: '14px',
        }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon size={14} color={iconColor || 'rgba(0,0,0,0.40)'} />
          <h4 style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700,
            color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: '0.09em',
          }}>{title}</h4>
        </div>
        {open ? <ChevronUp size={14} color="rgba(0,0,0,0.35)" /> : <ChevronDown size={14} color="rgba(0,0,0,0.35)" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── CAREER DETAIL DRAWER ────────────────────────────────────────────────────
export const CareerDetailDrawer = ({ career, analysisId, onClose }) => {
  const config = getHeatConfig(career.heat_level);
  const probPercent = Math.round(career.probability * 100);

  // On-demand detail fetch (Phase 2) — no competencies, just career-specific content
  const [detail, setDetail]               = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError]     = useState('');

  useEffect(() => {
    if (!analysisId) return;
    if (career.why_fit && career.trajectory) {
      setDetail({
        why_fit: career.why_fit, about_role: career.about_role,
        skills_gap: career.skills_gap, roadmap: career.roadmap,
        trajectory: career.trajectory,
      });
      return;
    }
    setDetailLoading(true);
    setDetailError('');
    axios.post(`${API}/career-detail`, {
      analysis_id: analysisId, career_id: career.id, career_title: career.title,
    }).then(res => setDetail(res.data))
      .catch(() => setDetailError('Could not load detailed analysis.'))
      .finally(() => setDetailLoading(false));
  }, [career.id, analysisId]); // eslint-disable-line

  return (
    <AnimatePresence>
      {career && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.25)', zIndex: 40 }} />

          <motion.div
            data-testid="career-detail-drawer"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
              position: 'fixed', right: 0, top: 0, bottom: 0,
              width: '500px', maxWidth: '100vw',
              backgroundColor: '#ffffff',
              borderLeft: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '-8px 0 40px rgba(0,0,0,0.10)',
              zIndex: 50, display: 'flex', flexDirection: 'column', overflowY: 'auto',
            }}
          >
            {/* ── Drawer Header ── */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.07)',
              position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    backgroundColor: config.badgeBg, border: `1px solid ${config.badgeBorder}`,
                    borderRadius: '9999px', padding: '3px 9px', marginBottom: '8px',
                  }}>
                    <TrendingUp size={10} color={config.badgeColor} />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600, color: config.textColor }}>
                      {probPercent}% match
                    </span>
                  </div>
                  <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '20px', fontWeight: 700, color: '#111111', lineHeight: '1.2' }}>
                    {career.title}
                  </h2>
                </div>
                <button
                  data-testid="career-detail-close-button"
                  onClick={onClose}
                  style={{
                    background: 'none', border: '1px solid rgba(0,0,0,0.10)', borderRadius: '8px',
                    padding: '8px', cursor: 'pointer', color: 'rgba(0,0,0,0.40)',
                    display: 'flex', alignItems: 'center', transition: 'border-color 0.2s ease', flexShrink: 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.25)'; e.currentTarget.style.color = '#111'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.10)'; e.currentTarget.style.color = 'rgba(0,0,0,0.40)'; }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* ── Drawer Content ── */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '22px' }}>

              {/* CTC */}
              <div data-testid="career-detail-ctc" style={{
                backgroundColor: '#fffbf2', border: '1px solid rgba(232,160,32,0.22)',
                borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '14px',
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  backgroundColor: 'rgba(232,160,32,0.12)', border: '1px solid rgba(232,160,32,0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <TrendingUp size={18} color="#E8A020" />
                </div>
                <div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.45)',
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '3px' }}>
                    Expected CTC (Post MBA)
                  </p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '22px', fontWeight: 700,
                    color: '#E8A020', fontVariantNumeric: 'tabular-nums' }}>
                    {career.ctc_range}
                  </p>
                </div>
              </div>

            {/* ── Detail sections (loaded on click) ── */}
            {detailLoading && !detail && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '24px 0', color: 'rgba(0,0,0,0.45)',
                fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
              }}>
                <Loader2 size={18} color="#E8A020" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                Building your personalised career report…
              </div>
            )}

              {/* About the Role */}
              {(detail?.about_role || detailLoading) && (
              <Section icon={Briefcase} title="About the Role">
                {detailLoading && !detail ? null : (
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.65)',
                  lineHeight: '1.65', backgroundColor: '#f8f8f8', border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: '10px', padding: '14px' }}>
                  {detail?.about_role}
                </p>
                )}
              </Section>
              )}

              {/* Why You Fit */}
              {(detail?.why_fit || detailLoading) && (
              <Section icon={Target} iconColor="#3FA86A" title="Why You Fit">
                {detailLoading && !detail ? null : (
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.65)',
                  lineHeight: '1.65', backgroundColor: 'rgba(63,168,106,0.06)',
                  border: '1px solid rgba(63,168,106,0.15)', borderRadius: '10px', padding: '14px' }}>
                  {detail?.why_fit}
                </p>
                )}
              </Section>
              )}

              {/* Skills to Develop */}
              {(detail?.skills_gap?.length > 0) && (
                <Section icon={AlertCircle} iconColor="#E8A020" title="Skills to Develop">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                    {detail.skills_gap.map((skill, i) => (
                      <span key={i} style={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 500,
                        color: '#9A6200', backgroundColor: 'rgba(232,160,32,0.08)',
                        border: '1px solid rgba(232,160,32,0.20)', borderRadius: '9999px', padding: '4px 10px',
                      }}>{skill}</span>
                    ))}
                  </div>
                </Section>
              )}

              {/* Skill Assessment */}
              <Section icon={BarChart2} iconColor="#E8A020" title="Skill Assessment">
                {detailLoading && !detail ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '16px 0', color: 'rgba(0,0,0,0.40)',
                    fontFamily: 'DM Sans, sans-serif', fontSize: '14px' }}>
                    <Loader2 size={15} color="#E8A020" style={{ animation: 'spin 1s linear infinite' }} />
                    Analysing your competencies…
                  </div>
                ) : detailError ? (
                  <p style={{ fontFamily: 'DM Sans', fontSize: '14px', color: '#D94040' }}>{detailError}</p>
                ) : detail?.competencies?.length > 0 ? (
                  <SkillAssessment competencies={detail.competencies} />
                ) : null}
              </Section>

              {/* Career Trajectory */}
              <Section icon={GitBranch} iconColor="#3FA86A" title="Career Trajectory">
                {detailLoading && !detail ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '16px 0', color: 'rgba(0,0,0,0.40)',
                    fontFamily: 'DM Sans, sans-serif', fontSize: '14px' }}>
                    <Loader2 size={15} color="#3FA86A" style={{ animation: 'spin 1s linear infinite' }} />
                    Mapping your career trajectory…
                  </div>
                ) : detailError ? null : detail?.trajectory ? (
                  <CareerTrajectory trajectory={detail.trajectory} />
                ) : null}
              </Section>

              {/* Roadmap */}
              {(detail?.roadmap?.length > 0 || detailLoading) && (
              <Section icon={MapPin} title="Career Roadmap" data-testid="career-detail-roadmap">
                {detailLoading && !detail ? null : (
                detail?.roadmap?.map((step, i) => (
                  <RoadmapStep key={step.step} step={step} isLast={i === detail.roadmap.length - 1} />
                ))
                )}
              </Section>
              )}
            </div>

            {/* Footer CTA */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid rgba(0,0,0,0.07)',
              marginTop: 'auto', backgroundColor: '#fafafa',
            }}>
              <a href="https://mastersunion.org/admissions" target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '12px 24px', backgroundColor: '#E8A020', border: 'none',
                  borderRadius: '9999px', cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 600, color: '#ffffff',
                  textDecoration: 'none', textAlign: 'center', transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Talk to Admissions
              </a>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.28)',
                textAlign: 'center', marginTop: '8px' }}>
                Masters' Union MBA · Delhi NCR
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
